import { AxiosError, AxiosResponse } from 'axios';
import { Responses } from 'constants/responses';
import { AppThunks } from 'modules/app/appActions';
import store from 'store';
import { Dispatch } from 'types/Store';
import dayjs from 'dayjs';
import { BrowserStorage } from 'utils/browserStorage';
import { ApiClientError, ApiClientErrorParams } from 'api/client';
import { getUserMessage } from 'utils/request';
import client from 'api/client/instance';
import RequestHeaders from 'app/constants/requestHeaders';
import { TokenRefreshService } from 'app/modules/auth/utils/tokenRefreshService';
import { isAiHubBaseURL } from 'app/config/aiHubEndpointMode';
import Logger from 'utils/logger';
import { requestUpgrade } from 'api/client/paymentMethodUpgradeBroker';
import {
  getRequestStaleMessage,
  getSomethingWentWrongMessage,
  getUpgradeCancelledMessage,
} from 'api/client/upgradeMessages';

const isUnpaidAccess = (status: number) => {
  return status === Responses.ERROR.PAYMENT_REQUIRED;
};

const isUnauthorizedAccess = (status: number) => {
  return status === Responses.ERROR.UNAUTHORIZED;
};

const isNotJSONRespnse = (headers: any) => {
  return headers['content-type']?.indexOf('application/json');
};

const isNotStatusTrue = (response: AxiosResponse) => {
  return (
    response.data &&
    typeof response.data === 'object' &&
    'status' in response.data &&
    response.data.status === false
  );
};

const isNotSuccessStatusCode = (status: any) => {
  return !Object.values(Responses.SUCCESSFUL).includes(status);
};

export const isReportableRejection = (response?: AxiosResponse): boolean =>
  typeof response?.status === 'number' &&
  response.status >= Responses.ERROR.INTERNAL_SERVER_ERRROR;

const normalizeUrlForGrouping = (url?: string): string =>
  (url?.split('?')[0] ?? 'unknown').replace(/\/\d+/g, '/:id');

export const buildRejectionError = (response: AxiosResponse): Error =>
  new Error(
    `Rejected response ${response.status}: ` +
      `${
        response.config?.method?.toUpperCase() ?? 'GET'
      } ${normalizeUrlForGrouping(response.config?.url)}`,
  );

const isAiHubServiceRequest = (response: AxiosResponse) => {
  return isAiHubBaseURL(response.config.baseURL);
};

const PAYMENT_METHOD_UPGRADE_ERROR = 'payment_method_upgrade_required';
const IDEMPOTENCY_KEY_REQUIRED_ERROR = 'idempotency_key_required';
const IDEMPOTENCY_KEY_CONFLICT_ERROR = 'idempotency_key_conflict';

/**
 * The 409 `payment_method_upgrade_required` wall — emitted by the BE
 * `EnforcePaymentMethodCurrency` middleware for Braintree-vaulted accounts
 * on any billing write. Matches strictly on status + body.error so a generic
 * 409 (e.g. idempotency_key_conflict) flows through the normal error path.
 */
export const isPaymentMethodUpgradeRequired = (
  response: AxiosResponse,
): boolean => {
  if (response.status !== 409) return false;
  const body = response.data;
  return (
    body !== null &&
    typeof body === 'object' &&
    'error' in body &&
    (body as { error?: unknown }).error === PAYMENT_METHOD_UPGRADE_ERROR
  );
};

const rejectAsUpgradeCancelled = (axiosError: AxiosError) =>
  Promise.reject(
    new ApiClientError({
      name: 'PaymentMethodUpgradeRequired',
      message: 'Payment method upgrade was cancelled by the user.',
      userMessage: getUpgradeCancelledMessage(),
      axiosError,
    }),
  );

const rejectAsUpgradeFailed = (axiosError: AxiosError) =>
  Promise.reject(
    new ApiClientError({
      name: 'PaymentMethodUpgradeRequired',
      message: 'Payment method upgrade did not resolve 409.',
      userMessage: getUpgradeCancelledMessage(),
      axiosError,
    }),
  );

/**
 * Pause the failed request, open the Stripe attach modal via the broker, and
 * either replay the original request under its existing `Idempotency-Key`
 * (success) or reject with a distinct ApiClientError name (cancel / loop).
 */
export async function handlePaymentMethodUpgradeRequired(
  axiosError: AxiosError,
) {
  const originalRequest = axiosError.config;
  if (!originalRequest) {
    Logger.error(
      '[Billing][409] No request config on AxiosError; cannot retry',
    );
    return rejectAsUpgradeFailed(axiosError);
  }

  if (
    (originalRequest as { _isRetryAfterUpgrade?: boolean })._isRetryAfterUpgrade
  ) {
    Logger.error(
      `[Billing][409] Got 409 again after Stripe attach succeeded: ${originalRequest.method?.toUpperCase()} ${
        originalRequest.url
      }`,
    );
    return rejectAsUpgradeFailed(axiosError);
  }

  Logger.log(
    `[Billing][409] payment_method_upgrade_required for ${originalRequest.method?.toUpperCase()} ${
      originalRequest.url
    }; awaiting modal`,
  );
  const outcome = await requestUpgrade();
  if (outcome === 'cancelled') {
    Logger.log(
      '[Billing][409] Upgrade modal cancelled; rejecting original request',
    );
    return rejectAsUpgradeCancelled(axiosError);
  }

  (originalRequest as { _isRetryAfterUpgrade?: boolean })._isRetryAfterUpgrade =
    true;
  Logger.log(
    `[Billing][409] Upgrade success; retrying ${originalRequest.method?.toUpperCase()} ${
      originalRequest.url
    }`,
  );
  return client(originalRequest);
}

/**
 * 428 — server requires Idempotency-Key on this endpoint but the FE didn't
 * send one. This is an FE bug: log to observability and surface a generic
 * "something went wrong" to the user. No auto-retry — risk of double-charge.
 */
export const isIdempotencyKeyRequiredError = (
  response: AxiosResponse,
): boolean => {
  if (response.status !== 428) return false;
  const body = response.data;
  return (
    body !== null &&
    typeof body === 'object' &&
    'error' in body &&
    (body as { error?: unknown }).error === IDEMPOTENCY_KEY_REQUIRED_ERROR
  );
};

/**
 * No `config`-undefined guard (cf. `handlePaymentMethodUpgradeRequired`):
 * this handler never retries the request, only rejects it, so an absent
 * `config` would harmlessly log `undefined` for url/method. The pattern
 * intentionally diverges from B's handler for that reason.
 */
export const handleIdempotencyKeyRequired = (
  axiosError: AxiosError,
): Promise<never> => {
  Logger.error('Idempotency-Key required but not sent — FE bug', {
    url: axiosError.config?.url,
    method: axiosError.config?.method,
  });

  return Promise.reject(
    new ApiClientError({
      name: 'IdempotencyKeyRequired',
      message: axiosError.message,
      userMessage: getSomethingWentWrongMessage(),
      axiosError,
    }),
  );
};

/**
 * 409 — same Idempotency-Key was reused with a different request body. This
 * is an FE state-management bug; "refresh and try again" is the safest user
 * recovery because reusing the same client-side intent would collide again.
 */
export const isIdempotencyKeyConflictError = (
  response: AxiosResponse,
): boolean => {
  if (response.status !== 409) return false;
  const body = response.data;
  return (
    body !== null &&
    typeof body === 'object' &&
    'error' in body &&
    (body as { error?: unknown }).error === IDEMPOTENCY_KEY_CONFLICT_ERROR
  );
};

/**
 * No `config`-undefined guard (cf. `handlePaymentMethodUpgradeRequired`):
 * reject-only handler — see `handleIdempotencyKeyRequired` JSDoc for the
 * rationale.
 */
export const handleIdempotencyKeyConflict = (
  axiosError: AxiosError,
): Promise<never> => {
  Logger.error(
    'Idempotency-Key conflict — same key reused with different body',
    {
      url: axiosError.config?.url,
      method: axiosError.config?.method,
    },
  );

  return Promise.reject(
    new ApiClientError({
      name: 'IdempotencyKeyConflict',
      message: axiosError.message,
      userMessage: getRequestStaleMessage(),
      axiosError,
    }),
  );
};

const rejectUnauthorizeAccess = async (response: AxiosResponse) => {
  Logger.warn(
    `[Auth][401] Unauthorized response: ${response.config.method?.toUpperCase()} ${
      response.config.url
    } (baseURL: ${response.config.baseURL})`,
  );

  if (isAiHubServiceRequest(response)) {
    Logger.log('[Auth][401] AI Hub request, skipping refresh');
    return rejectResponse({ message: 'Unauthorized access', response });
  }

  const originalRequest = response.config;

  if ((originalRequest as any)._isRetryAfterRefresh) {
    Logger.error('[Auth][401] Already retried after refresh, logging out user');
    store.dispatch<Dispatch>(AppThunks.logoutUser());
    return rejectResponse({ message: 'Unauthorized access', response });
  }

  try {
    Logger.log('[Auth][401] Attempting token refresh before retry...');
    await TokenRefreshService.refresh();

    (originalRequest as any)._isRetryAfterRefresh = true;

    if (originalRequest.headers) {
      delete originalRequest.headers[RequestHeaders.HttpJwtToken];
      delete originalRequest.headers[RequestHeaders.Authorization];
    }

    Logger.log(
      `[Auth][401] Retrying request: ${originalRequest.method?.toUpperCase()} ${
        originalRequest.url
      }`,
    );
    return client(originalRequest);
  } catch (error: unknown) {
    const status =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;
    const isAuthFailure = status === 401 || status === 403;

    Logger.error(
      `[Auth][401] Refresh failed (status=${status}, isAuthFailure=${isAuthFailure}): ${
        error instanceof Error ? error.message : String(error)
      }`,
    );

    if (isAuthFailure) {
      Logger.error('[Auth][401] Auth failure after refresh, logging out user');
      store.dispatch<Dispatch>(AppThunks.logoutUser());
    }

    return rejectResponse({ message: 'Unauthorized access', response });
  }
};

const rejectNotJSONResponse = (
  response: AxiosResponse,
  axiosError?: AxiosError,
) => {
  return rejectResponse({
    message: 'Server not responded with JSON response',
    response,
    axiosError,
  });
};

const rejectNotStatusTrue = (
  response: AxiosResponse,
  axiosError?: AxiosError,
) => {
  return rejectResponse({
    message: 'Server not responded with status true',
    response,
    axiosError,
  });
};

const rejectNotSuccessStatusCode = (
  response: AxiosResponse,
  axiosError?: AxiosError,
) => {
  return rejectResponse({
    message: 'Server not responded with success like status code',
    response,
    axiosError,
  });
};

const rejectUnpaidAccess = () => {
  const reloadedDate = BrowserStorage.get(
    BrowserStorage.keys.ExpiredReloadDate,
  );
  const today = dayjs().format('YYYY-MM-DD');

  if (reloadedDate !== today) {
    BrowserStorage.set(BrowserStorage.keys.ExpiredReloadDate, today);
    return (window.location.href = '/');
  }
};

const rejectResponse = (params: ApiClientErrorParams) => {
  return Promise.reject(
    new ApiClientError({
      userMessage: getUserMessage(params.response),
      ...params,
    }),
  );
};

function isBinaryResponse(response: AxiosResponse): boolean {
  const rt = response.config.responseType;
  return rt === 'blob' || rt === 'arraybuffer';
}

export const shouldRejectWrongResponse = (response: AxiosResponse) => {
  const result = isBinaryResponse(response)
    ? isUnpaidAccess(response.status) ||
      isUnauthorizedAccess(response.status) ||
      isNotSuccessStatusCode(response.status)
    : isUnpaidAccess(response.status) ||
      isUnauthorizedAccess(response.status) ||
      isNotJSONRespnse(response.headers) ||
      isNotStatusTrue(response) ||
      isNotSuccessStatusCode(response.status);

  if (result) {
    Logger.warn(
      `[Auth][Middleware] shouldRejectWrongResponse=true for ${response.config?.method?.toUpperCase()} ${
        response.config?.url
      }: status=${response.status}, unpaid=${isUnpaidAccess(
        response.status,
      )}, unauthorized=${isUnauthorizedAccess(
        response.status,
      )}, notJSON=${isNotJSONRespnse(
        response.headers,
      )}, notStatusTrue=${isNotStatusTrue(
        response,
      )}, notSuccessCode=${isNotSuccessStatusCode(response.status)}`,
    );
  }

  return result;
};

/**
 * @param axiosError  Present when this rejection came from an actual HTTP
 *   error (the `rejectExtender` path). It is forwarded onto the resulting
 *   `ApiClientError` so callers can branch on `error.axiosError.response.status`
 *   — without it, every status-specific handler in the app is unreachable,
 *   because this function claims *all* non-2xx JSON responses before
 *   `rejectExtender` gets to build an ApiClientError of its own. Absent when
 *   called from `responseChecker`, where the transport succeeded and only the
 *   payload was wrong.
 */
export const rejectWrongResponse = (
  response: AxiosResponse,
  axiosError?: AxiosError,
) => {
  if (isBinaryResponse(response)) {
    if (isUnpaidAccess(response.status)) {
      return rejectUnpaidAccess();
    }
    if (isUnauthorizedAccess(response.status)) {
      return rejectUnauthorizeAccess(response);
    }
    if (isNotSuccessStatusCode(response.status)) {
      return rejectNotSuccessStatusCode(response, axiosError);
    }
    return Promise.resolve(response);
  }
  if (isUnpaidAccess(response.status)) {
    return rejectUnpaidAccess();
  }

  if (isUnauthorizedAccess(response.status)) {
    return rejectUnauthorizeAccess(response);
  }

  if (isNotJSONRespnse(response.headers)) {
    return rejectNotJSONResponse(response, axiosError);
  }

  if (isNotStatusTrue(response)) {
    Logger.warn(
      `[Auth][Middleware] Rejecting response with status=false: ${response.config?.url}`,
      response.data,
    );
    return rejectNotStatusTrue(response, axiosError);
  }

  if (isNotSuccessStatusCode(response.status)) {
    return rejectNotSuccessStatusCode(response, axiosError);
  }
};
